import { getAttribute } from '@/presenters/components/search/Filters/utils/getAttribute';

import {
  FACETS_MAP,
  OFFER_FILTERS,
  PAGE_FILTERS,
  SORT_BY_FILTERS,
} from '../constants';
import { PRICE_URL_TEXT } from '../create-url/filters/price-range/price-range';
import {
  BEST_PRICE,
  LOWEST_PRICE,
} from '../create-url/filters/sort-by/constants';
import { getFiltersUrl } from '../create-url/rebuild-url/rebuild-url';
import { safeDecodeURIComponent } from '../create-url/utils/safe-decode-uri';

interface LocationProps {
  href: string;
}

type CategoryFiltersLabel = Record<string, string>;

interface AlgoliaFacetsObject {
  [key: string]: {
    prefix: string;
    sufix: string;
  };
}

interface AlgoliaState {
  refinementList?: Record<string, string[]>;
  toggle?: Record<string, boolean>;
  sortBy?: string;
  page?: number;
  range?: Record<string, string>;
}

interface Filters {
  [key: string]: string[];
}

interface FilterProcessingOptions {
  indexName: string;
  region: string;
  categoryFiltersLabel: CategoryFiltersLabel;
  algoliaFacetsObject: AlgoliaFacetsObject | {};
}

interface HandleRefinementListProps {
  key: string;
  values: string[];
  objectAlgolia: AlgoliaState;
  categoryFiltersLabel: CategoryFiltersLabel;
  algoliaFacetsObject: AlgoliaFacetsObject;
}

interface ProcessFilterValueProps {
  key: string;
  value: string;
  categoryFiltersLabel: CategoryFiltersLabel;
  algoliaFacetsObject: AlgoliaFacetsObject;
}

const parseFilterSegment = (segment: string): [string, string] => {
  const [key, value] = segment.split('=');
  const cleanedKey = key.replaceAll('filter[', '').replaceAll(']', '');
  const decodedValue = value.replace('_', ' ');

  return [cleanedKey, decodedValue];
};

const shouldParseQuerySegment = (keyValue: string): boolean => {
  const [key] = keyValue.split('=');
  const cleanedKey = key.replaceAll('filter[', '').replaceAll(']', '');

  return (
    keyValue.includes('=') &&
    (key.startsWith('filter[') ||
      PAGE_FILTERS.includes(cleanedKey) ||
      SORT_BY_FILTERS.includes(cleanedKey) ||
      OFFER_FILTERS.includes(cleanedKey))
  );
};

const mappedSegment = (index: number, keyValue: string): boolean => {
  return (
    index === 0 &&
    (!PAGE_FILTERS.some((filter) => keyValue.includes(filter)) ||
      SORT_BY_FILTERS.some((filter) => keyValue.includes(filter)) ||
      OFFER_FILTERS.some((filter) => keyValue.includes(filter)))
  );
};

const splitKeyValue = (keyValue: string): [string, string] => {
  if (keyValue.includes('=')) {
    const [key, ...values] = keyValue.split('=');
    return [key, values.join('/')];
  }

  const [key, ...values] = keyValue.split('/');

  return [key, values.join('/')];
};

const parseFiltersFromUrl = (filtersUrl: string[]): Filters => {
  return filtersUrl.reduce<Filters>((acc, keyValue, index) => {
    if (mappedSegment(index, keyValue)) {
      const [key, value] = splitKeyValue(keyValue);
      return { ...acc, [key]: [value] };
    }

    if (!shouldParseQuerySegment(keyValue)) {
      return acc;
    }

    const [key, value] = parseFilterSegment(keyValue);
    return {
      ...acc,
      [key]: acc[key] ? [...acc[key], value] : [value],
    };
  }, {});
};

const processFilterValue = (props: ProcessFilterValueProps): string => {
  const { key, value, categoryFiltersLabel, algoliaFacetsObject } = props;

  const attribute = categoryFiltersLabel[key];
  if (!attribute) return value.replace('_', ' ').trim();

  const { prefix = '', sufix = '' } = algoliaFacetsObject[attribute] || {};
  let processedValue = value;

  if (sufix) {
    processedValue = processedValue.replace(`_${sufix}`, '');

    if (processedValue.endsWith(sufix)) {
      processedValue = processedValue.substring(
        0,
        processedValue.length - sufix.length
      );
    }
  }
  if (prefix) {
    processedValue = processedValue.replace(`${prefix}_`, '');

    if (processedValue.startsWith(prefix)) {
      processedValue = processedValue.substring(prefix.length);
    }
  }

  return processedValue.includes('_')
    ? processedValue.replace(/_/g, ' ').trim()
    : processedValue.trim();
};

const getAlgoliaKey = (
  key: string,
  categoryFiltersLabel: CategoryFiltersLabel
): string => {
  if (FACETS_MAP[key]) {
    return FACETS_MAP[key];
  }

  return categoryFiltersLabel?.[key]
    ? 'attributes.' + categoryFiltersLabel[key]
    : 'attributes.' + key.charAt(0).toUpperCase() + key.slice(1);
};

const handleRefinementList = (props: HandleRefinementListProps) => {
  const {
    key,
    values,
    objectAlgolia,
    categoryFiltersLabel,
    algoliaFacetsObject,
  } = props;

  if (
    PAGE_FILTERS.some((filter) => key.includes(filter)) ||
    OFFER_FILTERS.some((filter) => key.includes(filter)) ||
    SORT_BY_FILTERS.some((filter) => key.includes(filter))
  ) {
    return;
  }

  if (!objectAlgolia.refinementList) {
    objectAlgolia.refinementList = {};
  }

  values.forEach((value) => {
    const processedValue = processFilterValue({
      key,
      value,
      categoryFiltersLabel,
      algoliaFacetsObject,
    });
    const algoliaKey = getAlgoliaKey(key, categoryFiltersLabel);

    if (objectAlgolia.refinementList![algoliaKey]) {
      objectAlgolia.refinementList![algoliaKey].push(processedValue);
    } else {
      objectAlgolia.refinementList![algoliaKey] = [processedValue];
    }
  });
};

const getPage = (key: string, value: string, objectAlgolia: AlgoliaState) => {
  if (key === 'page') {
    objectAlgolia.page = parseInt(value, 10) || 1;
  }
};
const getToggle = (
  key: string,
  objectAlgolia: AlgoliaState,
  region: string
) => {
  const toggleFilterObj = getAttribute(region).toggle;
  const toggleFilterEntry = Object.entries(toggleFilterObj).find(
    (obj) => obj[1].filterName === key
  );

  if (toggleFilterEntry) {
    objectAlgolia.toggle = {
      ...objectAlgolia.toggle,
      [toggleFilterEntry[0]]: true,
    };
  }
};

const getSortBy = (
  key: string,
  objectAlgolia: AlgoliaState,
  indexName: string,
  values: string[]
) => {
  if (key === LOWEST_PRICE || values[0] === LOWEST_PRICE) {
    objectAlgolia.sortBy = `${indexName}_price_asc`;
  }

  if (key === BEST_PRICE || values[0] === BEST_PRICE) {
    objectAlgolia.sortBy = `${indexName}_price_desc`;
  }
};

const getRange = (
  key: string,
  objectAlgolia: AlgoliaState,
  region: string,
  values: string[]
) => {
  if (key === PRICE_URL_TEXT) {
    const priceValue = values[0].includes('-')
      ? values[0].replace('-', ':')
      : ':' + values[0];

    objectAlgolia.range = {
      [`regionalAttributes.${region}.promotionalPrice`]: priceValue,
    };
  }
};

const buildAlgoliaObject = (
  filters: Filters,
  options: FilterProcessingOptions
): AlgoliaState => {
  const objectAlgolia: AlgoliaState = {};
  const { indexName, region, categoryFiltersLabel, algoliaFacetsObject } =
    options;

  Object.entries(filters).forEach(([key, values]) => {
    handleRefinementList({
      key,
      values,
      objectAlgolia,
      categoryFiltersLabel,
      algoliaFacetsObject,
    });
    getPage(key, values[0], objectAlgolia);
    getToggle(key, objectAlgolia, region);
    getSortBy(key, objectAlgolia, indexName, values);
    getRange(key, objectAlgolia, region, values);
  });

  return objectAlgolia;
};

const parseUrlContext = (
  location: LocationProps,
  indexName: string,
  region: string,
  categoryFiltersLabel = {},
  algoliaFacetsObject = {}
) => {
  const { href } = location;
  const urlDecoded = safeDecodeURIComponent(href);
  const filtersUrl = getFiltersUrl(urlDecoded);

  if (!filtersUrl) {
    return { [indexName]: {} };
  }

  const filters = parseFiltersFromUrl(filtersUrl);
  const objectAlgolia = buildAlgoliaObject(filters, {
    indexName,
    region,
    categoryFiltersLabel,
    algoliaFacetsObject,
  });

  return { [indexName]: objectAlgolia };
};

export default parseUrlContext;
